import React, { useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import VariablesExportModal from './VariablesExportModal'
import paletteOutline from '../../../assets/images/palette-outline.svg'
import Loader from '../shared/Loader'
import {
  ExportHeader,
  CollectInfo,
  ErrorAlert,
  CustomExportForm,
  useExportPage
} from '../shared/Export'
import {
  appendHiddenInputs,
  getResourcesFromUrl,
  resourceFilterInputs,
  splitSelection
} from '../ExportModal/utils/exportHelpers'
import { formatDate } from '../../utils/dateHelpers'

const EMPTY_PREFERENCES = {}

const shouldIgnoreFilters = returnUrl =>
  returnUrl && returnUrl.includes('/drafts') && !returnUrl.includes('/resources')

const ResourcesExport = ({
  collectId, draftId = null, returnUrl = null, returnTo = null, savedPreferences = EMPTY_PREFERENCES
}) => {
  const [showVariablesModal, setShowVariablesModal] = useState(false)
  const {
    loading,
    error,
    data,
    ensemble,
    selectedQuestions,
    setSelectedQuestions,
    totalSelectedQuestions
  } = useExportPage({ collectId, preferencesKey: 'excel', savedPreferences })

  const handleExport = useCallback(exportData => {
    const form = document.getElementById('export-form')
    const { sectionIds, questionIds } = splitSelection(selectedQuestions)

    const formInputs = [
      { name: 'section_ids', value: sectionIds.join(',') },
      { name: 'question_ids', value: questionIds.join(',') }
    ]

    if (exportData.variableIds)
      formInputs.push({ name: 'variable_ids', value: exportData.variableIds.join(',') })

    formInputs.push(...resourceFilterInputs(getResourcesFromUrl()))

    if (returnTo)
      formInputs.push({ name: 'return_to', value: returnTo })

    // When exporting from drafts index page (not resources page), ignore filters to export all resources
    if (shouldIgnoreFilters(returnUrl))
      formInputs.push({ name: 'ignore_filters', value: 'true' })

    appendHiddenInputs(form, formInputs)

    if (window.plausible)
      window.plausible('Collect Excel Exported')

    form.submit()
  }, [selectedQuestions, returnUrl, returnTo])

  const handleSubmit = useCallback(event => {
    event.preventDefault()

    if (data?.variables?.length === 0) {
      handleExport({
        variables: []
      })
    } else
      setShowVariablesModal(true)
  }, [data, handleExport])

  const handleModalClose = useCallback(() => setShowVariablesModal(false), [])

  const getRedirectBackUrl = useCallback(() => {
    if (returnUrl)
      return returnUrl
    if (draftId)
      return `/collects/${collectId}/drafts/${draftId}/resources`
    return '/collects'
  }, [collectId, draftId, returnUrl])

  const getExportUrl = useCallback(() => {
    const baseUrl = `/collects/${collectId}`
    return draftId ? `${baseUrl}/drafts/${draftId}/resources_exports` : `${baseUrl}/resources_exports`
  }, [collectId, draftId])

  if (loading)
    return <Loader text="Chargement des données..." size="small" />

  if (error)
    return <ErrorAlert message={error} />

  if (!data)
    return null
  const currentDraft = data.drafts.find(draftItem => draftItem.id === draftId)
  const startingAt = currentDraft?.starting_at ? formatDate(currentDraft.starting_at) : null

  return (
    <>
      <div className="modal-content border">
        <ExportHeader
          title="Personnaliser l'export Excel"
          icon={paletteOutline}
          iconAlt="Customize"
          cancelUrl={getRedirectBackUrl()}
        />
        <div className="modal-body">
          <CollectInfo
            collectName={data.name}
            startingAt={startingAt}
            ensembleName={ensemble?.name}
          />
          <CustomExportForm
            exportUrl={getExportUrl()}
            sections={data.sections}
            selectedQuestions={selectedQuestions}
            title="Sélectionnez les sections et questions à exporter au niveau de la collecte"
            onQuestionChange={setSelectedQuestions}
            onSubmit={handleSubmit}
          />
        </div>
        <div className="modal-footer">
          <div className="d-flex justify-content-end align-items-center w-100">
            <button
              type="submit"
              form="export-form"
              className="btn btn-success"
              disabled={totalSelectedQuestions === 0}
            >
              {data.variables?.length === 0 ? 'Exporter' : 'Suivant'}
            </button>
          </div>
        </div>
      </div>
      {showVariablesModal && data ? (
        <div
          className="modal fade show"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
          role="dialog"
          aria-modal="true"
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <VariablesExportModal
              variables={data.variables}
              collect={data}
              startingAt={startingAt}
              savedPreferences={savedPreferences.excel || {}}
              onClose={handleModalClose}
              onExport={handleExport}
            />
          </div>
        </div>
      ) : null}
    </>
  )
}

ResourcesExport.propTypes = {
  collectId: PropTypes.number.isRequired
}

export default ResourcesExport
